import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

DialogEditReconciliationView.propTypes = {
    item: PropTypes.object.isRequired,
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
};

function DialogEditReconciliationView({ item, onCancel, onDone }) {
    // MARK: ---- PROPERTIES ----
    const [loading, setLoading] = useState(false);
    const [readyToValidate, setReadyToValidate] = useState(false);
    const [amount, setAmount] = useState(0);
    const [taxRate, setTaxRate] = useState(0);
    const [revenue, setRevenue] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const selectFilePathList = useRef([]);
    const [paperDocumentFile, setPaperDocumentFile] = useState(null);
    const [invoiceDocumentFile, setInvoiceDocumentFile] = useState(null);
    const isSale = useMemo(() => {
        return item?.agency?.primaryRole === 'SALE';
    }, [item]);
    const isOrganizationAgency = useMemo(() => {
        return item?.agency?.type === 'ORGANIZATION';
    }, [item]);

    useEffect(() => {
        if (item) {
            setAmount(Math.round(item?.amount));
            setTaxRate(item?.taxRate * 100);
            setSelectedFiles(item?.attachedFiles ?? []);
            setPaperDocumentFile(item?.document);
        }
    }, [item]);

    useEffect(() => {
        if (!isNaN(amount)) {
            if (!isOrganizationAgency) {
                setRevenue(Math.round(amount * (1 - taxRate / 100)));
            } else {
                setRevenue(Math.round(amount));
            }
        } else {
            setRevenue(0);
        }
    }, [amount, taxRate, isOrganizationAgency]);

    const labelMap = {
        SALE: {
            amount: 'Tổng hoa hồng',
            revenue: 'Tổng hoa hồng đại lý được nhận'
        },
        INVESTOR: {
            amount: 'Doanh thu theo phương tiện',
            revenue: 'Doanh thu NĐT được nhận'
        },
        GROUP: {
            amount: 'Doanh thu theo phương tiện',
            revenue: 'Doanh thu NĐT được nhận'
        },
        LANDLORD: {
            amount: 'Doanh thu/phí thuê theo mặt bằng',
            revenue: 'Doanh thu chủ mặt bằng được nhận'
        }
    }

    const roleLabels = labelMap[item?.agency?.primaryRole] || {};

    // MARK: ---- METHODS ----

    const handleSubmit = async () => {
        setReadyToValidate(true);

        if (item?.reconciliationType === AgencyConstants.ReconciliationType.PAPER) {
            if (!paperDocumentFile || !Utils.isValid(amount) || !isOrganizationAgency && !Utils.isValid(taxRate)) {
                ToastHelper.showError('Vui lòng điền đủ thông tin');
                return;
            }
            if (Utils.isValid(amount) && amount < 0) {
                ToastHelper.showError('Tổng số tiền phải lớn hơn hoặc bằng 0');
                return;
            }
            if (!isOrganizationAgency && Utils.isValid(taxRate) && (taxRate < 0 || taxRate > 100)) {
                ToastHelper.showError('Thuế phải nằm trong khoảng từ 0 đến 100');
                return;
            }
            if (isOrganizationAgency && !Utils.isValid(invoiceDocumentFile)) {
                ToastHelper.showError('Vui lòng điền đủ thông tin');
                return;
            }
        }

        if (!_.isEmpty(selectedFiles)) {
            selectFilePathList.current = [];
            await processSelectedFiles(selectedFiles);
        }
        setLoading(true);

        if (invoiceDocumentFile) {
            const formData = new FormData();
            formData.append('reconciliationId', item?.reconciliationId);
            formData.append('files', invoiceDocumentFile);

            await AgencyHelper.API.requestUploadInvoiceDocument(formData);
        }

        if (item?.reconciliationType === AgencyConstants.ReconciliationType.PAPER) {
            if (paperDocumentFile) {
                const formData = new FormData();
                formData.append('files', paperDocumentFile);
                formData.append('reconciliationId', item?.reconciliationId);
                formData.append('status', 'COMPLETED');
                formData.append('amount', amount);
                if (!isOrganizationAgency) {
                    formData.append('taxRate', taxRate / 100);
                }
                formData.append('netAmount', Math.round(!isOrganizationAgency ? amount * (1 - taxRate / 100) : amount));

                const isSuccess = await AgencyHelper.API.requestUploadPaperReconciliation(formData);

                if (isSuccess && _.isBoolean(isSuccess)) {
                    onDone();
                } else {
                    ToastHelper.showError(isSuccess);
                }
            }
        } else {
            const params = {
                document: '',
                attachedFiles: selectFilePathList.current,
                amount,
                taxRate: taxRate / 100,
                netAmount: Math.round(!isOrganizationAgency ? amount * (1 - taxRate / 100) : amount),
            };
            const isSuccess = await AgencyHelper.API.requestUpdateReconciliation(item?.reconciliationId, params);

            if (isSuccess && _.isBoolean(isSuccess)) {
                onDone();
            } else {
                ToastHelper.showError(isSuccess);
            }
        }
        setLoading(false);
    };

    const handleFilesChanged = (e) => {
        const newFiles = Array.from(e.target.files || []);
        if (!newFiles.length) return;

        setSelectedFiles((prev) => {
            const validNew = newFiles.filter((file) => {
                if (file.name.length > 128) {
                    ToastHelper.showError(`Tên file quá dài! Tối đa cho phép 128 ký tự.`);
                    return false;
                }
                return true;
            });

            if (!validNew.length) return prev;

            const allFiles = [...prev, ...validNew];

            return allFiles.filter((file, idx, arr) =>
                idx === arr.findIndex((f) => {
                    if (typeof f === 'string' && typeof file === 'string') return f === file;
                    if (f instanceof File && file instanceof File) {
                        return f.name === file.name && f.size === file.size;
                    }
                    return false;
                })
            );
        });

        e.target.value = null;
    };

    const handleRemoveFile = (file) => {
        setSelectedFiles(prevFiles =>
            prevFiles.filter(item =>
                typeof file === 'string'
                    ? item !== file
                    : !(item instanceof File && item.lastModified === file.lastModified)
            )
        );
    };

    async function processSelectedFiles(files) {
        for (const file of files) {
            const path = await uploadAsyncFunction(file);
            selectFilePathList.current?.push(path);
        }
    }
    async function uploadAsyncFunction(file) {
        return new Promise((resolve) => {
            if (file instanceof File) {
                miscHelper.callApiUploadFile(file).then((mediaList) => {
                    if (_.isArray(mediaList) && mediaList.length > 0) {
                        const firstMedia = _.first(mediaList);
                        const relativePath = firstMedia?.relativePath;
                        if (relativePath) {
                            resolve(relativePath);
                        } else {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                });
            } else {
                resolve(file);
            }
        });
    }

    return (
        <div className="pt-4">
            <div className="d-flex flex-column gap-3 m-0 p-0 px-4">
                <div>
                    {UIHelper.buildFormLabel(roleLabels.amount, true)}
                    <KTFormInput
                        name="amount"
                        value={amount}
                        onChange={(value) => {
                            const parsedValue = typeof value === 'string'
                                ? parseFloat(value.replace(',', '.'))
                                : value;

                            setAmount(parsedValue);
                        }}
                        onBlur={readyToValidate}
                        enableCheckValid
                        type={KTFormInputType.currency}
                        placeholder={`Nhập ${roleLabels.amount?.toLowerCase()}`}
                        isValid={Utils.isValid(amount) && amount > 0}
                        isTouched={readyToValidate}
                        feedbackText={
                            !Utils.isValid(amount)
                                ? `Vui lòng nhập ${roleLabels.amount?.toLowerCase()}`
                                : amount <= 0
                                    ? `${roleLabels.amount} phải lớn hơn 0`
                                    : ''
                        }
                        disabled={true}
                    />
                </div>
                {!isOrganizationAgency && (
                    <div>
                        {UIHelper.buildFormLabel('Thuế TNCN', true)}
                        < KTFormInput
                            name="taxRate"
                            value={taxRate}
                            onChange={(value) => {
                                setTaxRate(value);
                            }}
                            onBlur={readyToValidate}
                            enableCheckValid
                            unit='%'
                            type={KTFormInputType.currency}
                            placeholder={'Nhập thuế TNCN'}
                            isValid={Utils.isValid(taxRate) && taxRate >= 0 && taxRate <= 100}
                            isTouched={readyToValidate}
                            feedbackText={!Utils.isValid(taxRate)
                                ? 'Vui lòng nhập thuế TNCN'
                                : (taxRate < 0 || taxRate > 100) ? 'Thuế TNCN phải nằm trong khoảng từ 0 đến 100' : ''
                            }
                            disabled={true}
                        />
                    </div>
                )}
                <div>
                    {UIHelper.buildFormLabel(roleLabels.revenue, true)}
                    <KTFormInput
                        name="revenue"
                        value={revenue}
                        onChange={(value) => { }}
                        onBlur={readyToValidate}
                        enableCheckValid
                        type={KTFormInputType.currency}
                        placeholder={'Nhập tổng số tiền'}
                        isValid={Utils.isValid(amount) && amount > 0}
                        isTouched={readyToValidate}
                        feedbackText={!Utils.isValid(amount)
                            ? 'Vui lòng nhập tổng số tiền'
                            : amount <= 0 ? 'Tổng số tiền phải lớn hơn 0' : ''
                        }
                        disabled={true}
                    />
                </div>

                {/* file dinh kem */}
                {item?.reconciliationType === AgencyConstants.ReconciliationType.DIGITAL && (
                    <div className="mb-2">
                        <div className="">{UIHelper.buildFormLabel('Tập tin đính kèm')}</div>
                        <div>
                            {!_.isEmpty(selectedFiles) && (
                                <div className="row m-0 p-0">
                                    {selectedFiles.map((item, index) => {
                                        return (
                                            <div className="p-0 col-6 mb-2 p-2" key={item?.lastModified ?? item ?? index}>
                                                <ImageView
                                                    name={item?.lastModified ?? item ?? index}
                                                    path={_.isString(item) ? item : item?.name}
                                                    onRemove={() => {
                                                        handleRemoveFile(item);
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="d-flex">
                                <label className="">
                                    {UIHelper.buildNegativeButton(
                                        'Thêm tập tin',
                                        () => { },
                                        <i className="fa-regular fa-upload" />
                                    )}
                                    <input
                                        type="file"
                                        accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                                        onChange={handleFilesChanged}
                                        multiple
                                        hidden
                                        onClick={(e) => {
                                            e.target.value = null;
                                        }}
                                    />
                                </label>
                            </div>
                        </div>
                    </div>
                )}
                {item?.reconciliationType === AgencyConstants.ReconciliationType.PAPER && (
                    <div className='row px-2 mb-2'>
                        {/* file doi soat */}
                        <div className="col-12">
                            <div>
                                {UIHelper.buildFormLabel('Tải lên file đối soát scan', true)}
                                <label className="">
                                    {UIHelper.buildNegativeButton(
                                        'Thêm tập tin',
                                        () => { },
                                        <i className="fa-regular fa-upload" />
                                    )}
                                    <input
                                        type="file"
                                        accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                                        onChange={(e) => {
                                            const files = e.target.files;
                                            if (!files || files.length === 0) return;
                                            const file = files[0];
                                            if (file.name.length > 128) {
                                                ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                                e.target.value = null;
                                                return;
                                            }
                                            setPaperDocumentFile(file);
                                        }}
                                        hidden
                                        onClick={(e) => {
                                            e.target.value = null;
                                        }}
                                    />
                                </label>
                                {paperDocumentFile && (
                                    <div className='col-6 px-0'>
                                        <ImageView
                                            name={paperDocumentFile?.lastModified ?? paperDocumentFile}
                                            path={
                                                _.isString(paperDocumentFile)
                                                    ? paperDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                    : paperDocumentFile?.name
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* file hoa don thue */}
                        {isOrganizationAgency && (
                            <div className="col-12">
                                <div>
                                    {UIHelper.buildFormLabel('Hóa đơn thuế', true)}
                                    <label className="">
                                        {UIHelper.buildNegativeButton(
                                            'Thêm tập tin',
                                            () => { },
                                            <i className="fa-regular fa-upload" />
                                        )}
                                        <input
                                            type="file"
                                            accept={['.pdf']}
                                            onChange={(e) => {
                                                const files = e.target.files;
                                                if (!files || files.length === 0) return;
                                                const file = files[0];
                                                if (file.name.length > 128) {
                                                    ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                                    e.target.value = null;
                                                    return;
                                                }
                                                setInvoiceDocumentFile(file);
                                            }}
                                            hidden
                                            onClick={(e) => {
                                                e.target.value = null;
                                            }}
                                        />
                                    </label>
                                    {invoiceDocumentFile && (
                                        <div className='col-6 px-0'>
                                            <ImageView
                                                name={invoiceDocumentFile?.lastModified ?? invoiceDocumentFile}
                                                path={
                                                    _.isString(invoiceDocumentFile)
                                                        ? invoiceDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                        : invoiceDocumentFile?.name
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div
                className="row px-4 py-3"
                style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
            >
                <div className="col-6 pr-2">
                    {UIHelper.buildNegativeButton('Hủy bỏ', onCancel)}
                </div>
                <div className="col-6 pl-2">
                    {UIHelper.buildGreenButton(
                        'Lưu lại',
                        handleSubmit,
                        null,
                        false,
                        loading
                    )}
                </div>
            </div>
        </div >
    );
}

export default DialogEditReconciliationView;